import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import routeSeoConfig from '@/lib/route-seo.json';
import { ROUTE_CONTENT } from '@/lib/route-content';

// Per-route metadata resolves from the SAME config that the build-time
// prerender bakes into each route's static HTML (scripts/prerender-routes.mjs).
// Resolving it here rather than in each page is what actually makes that file a
// single source of truth: otherwise a page's hard-coded props would let Helmet
// restore stale values after hydration, so crawlers and browsers would show
// different metadata for the same URL.
type RouteSeo = {
  title: string;
  description: string;
  canonicalUrl: string;
  ogImage: string;
  h1: string;
};

const ROUTE_SEO = routeSeoConfig.routes as Record<string, RouteSeo>;
const FALLBACK_SEO = ROUTE_SEO['/'];

// React Router matches paths case-insensitively by default, so /Pricing renders
// the Pricing page. Key the lookup the same way, or that URL would render the
// right page under the homepage's metadata.
const ROUTE_SEO_BY_LOWER_PATH: Record<string, RouteSeo> = Object.fromEntries(
  Object.entries(ROUTE_SEO).map(([path, seo]) => [path.toLowerCase(), seo])
);

// Structured data resolves the same way, from src/lib/route-content.ts — the
// module the prerender also bundles to bake a JSON-LD <script> into each
// route's static HTML.
const ROUTE_JSON_LD_BY_LOWER_PATH: Record<string, object> = Object.fromEntries(
  Object.entries(ROUTE_CONTENT).map(([path, content]) => [path.toLowerCase(), content.jsonLd])
);

// The prerender tags the JSON-LD it bakes into the HTML with the route it was
// built for. On the landing route that script is already correct, so Helmet
// must NOT add a duplicate; after a client-side navigation it is stale, so it
// has to go before Helmet's replaces it.
const PRERENDERED_JSON_LD_SELECTOR = 'script[type="application/ld+json"][data-prerender-route]';

const prerenderedJsonLdRoute = (): string | null => {
  if (typeof document === 'undefined') return null;
  return document.querySelector(PRERENDERED_JSON_LD_SELECTOR)?.getAttribute('data-prerender-route') ?? null;
};

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  noIndex?: boolean;
  jsonLd?: object;
}

export const SEOHead = ({
  title,
  description,
  canonicalUrl,
  ogImage,
  ogType = "website",
  twitterCard = "summary_large_image",
  noIndex = false,
  jsonLd,
}: SEOHeadProps) => {
  const { pathname } = useLocation();
  // Trailing slashes never reach react-router's own links, but a pasted URL can
  // carry one; normalize so /pricing/ resolves the same entry as /pricing.
  const routeKey = (pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname).toLowerCase();
  const routeSeo = ROUTE_SEO_BY_LOWER_PATH[routeKey] ?? FALLBACK_SEO;

  // An explicit prop still wins — NotFound and any future one-off page need it —
  // but routed pages should omit these and inherit from the config.
  const resolvedTitle = title ?? routeSeo.title;
  const resolvedDescription = description ?? routeSeo.description;
  const resolvedCanonical = canonicalUrl ?? routeSeo.canonicalUrl;
  const resolvedOgImage = ogImage ?? routeSeo.ogImage;
  const resolvedJsonLd = jsonLd ?? ROUTE_JSON_LD_BY_LOWER_PATH[routeKey];

  const prerenderedRoute = prerenderedJsonLdRoute();
  const jsonLdAlreadyInHtml = prerenderedRoute !== null && prerenderedRoute.toLowerCase() === routeKey;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.querySelectorAll(PRERENDERED_JSON_LD_SELECTOR).forEach((el) => {
      if ((el.getAttribute('data-prerender-route') ?? '').toLowerCase() !== routeKey) el.remove();
    });
  }, [routeKey]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={resolvedCanonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:image" content={resolvedOgImage} />
      <meta property="og:image:alt" content={resolvedTitle} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Sidestep Ads" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={resolvedCanonical} />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />
      <meta name="twitter:image:alt" content={resolvedTitle} />

      {/* JSON-LD Structured Data — skipped when the prerender already baked
          this route's script into the HTML, so the page carries exactly one. */}
      {resolvedJsonLd && !jsonLdAlreadyInHtml && (
        <script type="application/ld+json">
          {JSON.stringify(resolvedJsonLd)}
        </script>
      )}
    </Helmet>
  );
};
